import { FormControl, FormLabel, Stack, Switch, TextField, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { isDurationString } from '@chronosphereio/core';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { useState } from 'react';
import { parseTemplateVariables, QueryOptionsDefinition } from '@/core';

export interface TimeOptionsEditorProps {
  time?: QueryOptionsDefinition['time'];
  onTimeChange: (newTime: QueryOptionsDefinition['time'] | undefined) => void;
}

const TIME_OPTION_TOGGLE_ID = 'time-option-toggle';
const TIME_OPTION_INPUT_ID = 'time-option-input';
const RELATIVE_TIME_TOOLTIP_HELP_TEXT = 'Set a relative time range for this panel. Overrides the dashboard time range.';

export function TimeOptionsEditor({ time, onTimeChange }: TimeOptionsEditorProps) {
  const [isRelativeTimeOverrideEnabled, setIsRelativeTimeOverrideEnabled] = useState(!!time?.start);
  const [relativeTimeValue, setRelativeTimeValue] = useState(time?.start ?? '');

  const hasVariables = parseTemplateVariables(relativeTimeValue).length > 0;
  const isValidRelativeTimeOption =
    !isRelativeTimeOverrideEnabled || relativeTimeValue === '' || isDurationString(relativeTimeValue) || hasVariables;

  const handleTimeValueSubmit = () => {
    if (relativeTimeValue && isValidRelativeTimeOption) {
      onTimeChange({ start: relativeTimeValue });
    } else {
      // Reset when relative time duration is invalid
      onTimeChange(undefined);
    }
  };

  return (
    <>
      <Typography variant="h4" color="text.secondary">
        Time preference
      </Typography>
      <FormControl fullWidth>
        <Stack direction="row" justifyContent="space-between">
          <FormLabel htmlFor={TIME_OPTION_TOGGLE_ID} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography color="text.primary">Override dashboard time</Typography>
          </FormLabel>
          <Switch
            id={TIME_OPTION_TOGGLE_ID}
            checked={isRelativeTimeOverrideEnabled}
            onChange={(__, checked) => {
              setIsRelativeTimeOverrideEnabled(checked);
              // Reset relative time option when using dashboard time
              if (!checked) {
                onTimeChange(undefined);
                setRelativeTimeValue('');
              }
            }}
          />
        </Stack>
      </FormControl>
      {isRelativeTimeOverrideEnabled && (
        <FormControl fullWidth sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="start" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              // Make sure the height of the label is the same as the input
              sx={{ height: '32px', lineHeight: '32px' }}
            >
              <FormLabel htmlFor={TIME_OPTION_INPUT_ID}>
                <Typography color="text.primary">Relative time override</Typography>
              </FormLabel>
              <Tooltip Content={RELATIVE_TIME_TOOLTIP_HELP_TEXT}>
                <InfoIcon aria-hidden={false} fontSize="inherit" color="secondary" />
              </Tooltip>
            </Stack>
            <TextField
              id={TIME_OPTION_INPUT_ID}
              placeholder="Ex. 15m, 1h, 7d"
              value={relativeTimeValue}
              onChange={(e) => setRelativeTimeValue(e.target.value)}
              error={!isValidRelativeTimeOption}
              helperText={!isValidRelativeTimeOption ? 'Invalid value. Use: 15m, 1h, 7d, etc.' : ''}
              onBlur={handleTimeValueSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTimeValueSubmit();
                }
              }}
            />
          </Stack>
        </FormControl>
      )}
    </>
  );
}
